import { useRef, useState, useEffect, useCallback } from 'react';
import { useT } from '@/i18n/useT';
import { Icon } from '@/ui/icons/Icon';
import { GRID_SIZE, screenToScene, snapPointToGrid, type Viewport } from './viewport';
import { useSceneEditorStore } from './store/sceneEditorStore';
import styles from './SceneView.module.css';
import { useProjectStore } from '@/store/projectStore';
import { useSimulationStore } from '@/store/simulationStore';
import { useUiStore } from '@/store/uiStore';
import type { Device, DeviceType } from '@/devices/types';
import type { DeviceId } from '@/core/types';
import { deviceRegistry } from '@/devices/catalog';
import { PilotLampSvg } from './components/PilotLampSvg';
import { PushButtonSvg } from './components/PushButtonSvg';
import { EmergencyStopSvg } from './components/EmergencyStopSvg';
import { FanSvg } from './components/FanSvg';
import { AcMotor3PhaseSvg } from './components/AcMotor3PhaseSvg';
import { TankSvg } from './components/TankSvg';
import { MixerSvg } from './components/MixerSvg';
import { PumpSvg } from './components/PumpSvg';
import { ValveSvg } from './components/ValveSvg';
import { LevelSensorSvg } from './components/LevelSensorSvg';
import { PIDSvg } from './components/PIDSvg';
import { PipeSvg } from './components/PipeSvg';
import { ConveyorSvg } from './components/ConveyorSvg';
import { SensorSvg } from './components/SensorSvg';
import {
  getDevicePorts,
  getPortWorldPosition,
  computePipeRoute,
  calculateDraggedPipeWaypoints,
  type PortDirection,
} from './components/pipePorts';
import type { PipeConnection, PipePortType } from '@/project/types';

const PATTERN_ID = 'scene-grid-pattern';

interface AddressBadgeProps {
  address?: string | undefined;
  cx: number;
  cy?: number | undefined;
  anchorX?: number | undefined;
  anchorY?: number | undefined;
  offsetX?: number | undefined;
  offsetY?: number | undefined;
  visible?: boolean | undefined;
  isDesignMode?: boolean | undefined;
  isSelected?: boolean | undefined;
  isDragging?: boolean | undefined;
  onPointerDown?: ((e: React.PointerEvent) => void) | undefined;
  onReset?: (() => void) | undefined;
  onSelect?: (() => void) | undefined;
}

function AddressBadge({
  address,
  cx,
  cy = -12,
  anchorX,
  anchorY,
  offsetX = 0,
  offsetY = 0,
  visible = true,
  isDesignMode = false,
  isSelected = false,
  isDragging = false,
  onPointerDown,
  onReset,
  onSelect,
}: AddressBadgeProps) {
  if (!address || visible === false) return null;

  const defaultX = cx;
  const defaultY = cy;
  const compAnchorX = anchorX ?? cx;
  const compAnchorY = anchorY ?? 30; // Direct anchor on component body
  const badgeX = defaultX + offsetX;
  const badgeY = defaultY + offsetY;

  const badgeWidth = Math.max(46, address.length * 8 + 14);
  const badgeHeight = 18;
  const halfW = badgeWidth / 2;
  const halfH = badgeHeight / 2;

  const isMoved = Math.abs(offsetX) > 16 || Math.abs(offsetY) > 16;

  return (
    <g className="device-address-badge-group">
      {/* Dynamic leader line starting directly ON the component body */}
      {isMoved && (
        <g style={{ pointerEvents: 'none' }}>
          <line
            x1={compAnchorX}
            y1={compAnchorY}
            x2={badgeX}
            y2={badgeY}
            stroke="#38bdf8"
            strokeWidth="1.2"
            strokeDasharray="3,3"
            opacity={0.7}
          />
          {/* Component Collar Ring */}
          <circle cx={compAnchorX} cy={compAnchorY} r={4} fill="#0f172a" stroke="#38bdf8" strokeWidth={1.5} opacity={0.9} />
          {/* Inner Dot on Component */}
          <circle cx={compAnchorX} cy={compAnchorY} r={2} fill="#38bdf8" opacity={0.95} />
        </g>
      )}

      {/* Interactive Badge */}
      <g
        transform={`translate(${badgeX}, ${badgeY})`}
        style={{
          cursor: isDesignMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
          pointerEvents: isDesignMode ? 'auto' : 'none',
          userSelect: 'none',
        }}
        onPointerDown={onPointerDown}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onReset?.();
        }}
      >
        <title>
          {isDesignMode
            ? `PLC Adresi: ${address}\n• Sürükleyerek istediğiniz yere taşıyabilirsiniz\n• Çift tıklayarak orijinal yerine sıfırlayabilirsiniz`
            : `PLC Adresi: ${address}`}
        </title>

        {/* Large invisible hit area for easy grabbing */}
        <rect
          x={-halfW - 4}
          y={-halfH - 4}
          width={badgeWidth + 8}
          height={badgeHeight + 8}
          fill="transparent"
        />

        {/* Badge Pill Body */}
        <rect
          x={-halfW}
          y={-halfH}
          width={badgeWidth}
          height={badgeHeight}
          rx="4"
          fill="#0f172a"
          stroke={isDragging ? '#38bdf8' : isSelected ? '#60a5fa' : isMoved ? '#0284c7' : '#334155'}
          strokeWidth={isDragging ? 1.8 : isSelected || isMoved ? 1.4 : 1}
          style={{
            filter: isDragging
              ? 'drop-shadow(0 0 6px rgba(56, 189, 248, 0.75))'
              : 'drop-shadow(0 2px 4px rgba(0,0,0,0.65))',
          }}
        />

        {/* Subtle Grip Dots in Design Mode */}
        {isDesignMode && (
          <g transform={`translate(${-halfW + 5}, -4)`} opacity={0.65}>
            <circle cx="0" cy="2" r="1" fill="#94a3b8" />
            <circle cx="0" cy="6" r="1" fill="#94a3b8" />
            <circle cx="3" cy="2" r="1" fill="#94a3b8" />
            <circle cx="3" cy="6" r="1" fill="#94a3b8" />
          </g>
        )}

        {/* Text */}
        <text
          x={isDesignMode ? 4 : 0}
          y={4}
          fill={isDragging ? '#38bdf8' : '#f8fafc'}
          fontSize="11"
          fontWeight="bold"
          textAnchor="middle"
          letterSpacing="0.5"
        >
          {address}
        </text>
      </g>
    </g>
  );
}

function getBadgeAnchor(d: Device): { cx: number; anchorX: number; anchorY: number } {
  if (d.type === 'pump' || (d.type === 'motor' && d.props['variant'] === 'pump_motor')) {
    return { cx: 60, anchorX: 65, anchorY: 48 };
  }
  if (d.type === 'tank') {
    return { cx: 65, anchorX: 65, anchorY: 40 };
  }
  if (d.type === 'valve') {
    return { cx: 50, anchorX: 50, anchorY: 42 };
  }
  if (d.type === 'fan' || (d.type === 'motor' && d.props['variant'] === 'fan')) {
    return { cx: 40, anchorX: 40, anchorY: 40 };
  }
  if (d.type === 'mixer' || (d.type === 'motor' && d.props['variant'] === 'mixer')) {
    return { cx: 25, anchorX: 25, anchorY: 35 };
  }
  if (d.type === 'motor') {
    return { cx: 75, anchorX: 50, anchorY: 45 };
  }
  if (d.type === 'sensor' || d.type === 'level-sensor' || d.type === 'jam-sensor') {
    return { cx: 22, anchorX: 22, anchorY: 22 };
  }
  if (d.type === 'pid-controller') {
    return { cx: 50, anchorX: 50, anchorY: 40 };
  }
  return { cx: 30, anchorX: 30, anchorY: 30 };
}

interface NameBadgeProps {
  name?: string | undefined;
  cx: number;
  cy: number;
  anchorX?: number | undefined;
  anchorY?: number | undefined;
  offsetX?: number | undefined;
  offsetY?: number | undefined;
  visible?: boolean | undefined;
  isDesignMode?: boolean | undefined;
  isSelected?: boolean | undefined;
  isDragging?: boolean | undefined;
  onPointerDown?: ((e: React.PointerEvent) => void) | undefined;
  onReset?: (() => void) | undefined;
  onSelect?: (() => void) | undefined;
}

function NameBadge({
  name,
  cx,
  cy,
  anchorX,
  anchorY,
  offsetX = 0,
  offsetY = 0,
  visible = true,
  isDesignMode = false,
  isSelected = false,
  isDragging = false,
  onPointerDown,
  onReset,
  onSelect,
}: NameBadgeProps) {
  const displayName = name?.trim();
  if (!displayName || visible === false) return null;

  const defaultX = cx;
  const defaultY = cy;
  const compAnchorX = anchorX ?? cx;
  const compAnchorY = anchorY ?? 30;
  const badgeX = defaultX + offsetX;
  const badgeY = defaultY + offsetY;

  const badgeWidth = Math.max(52, displayName.length * 7.5 + 22);
  const badgeHeight = 19;
  const halfW = badgeWidth / 2;
  const halfH = badgeHeight / 2;

  const isMoved = Math.abs(offsetX) > 16 || Math.abs(offsetY) > 16;

  return (
    <g className="device-name-badge-group">
      {/* Dynamic leader line starting directly ON the component body */}
      {isMoved && (
        <g style={{ pointerEvents: 'none' }}>
          <line
            x1={compAnchorX}
            y1={compAnchorY}
            x2={badgeX}
            y2={badgeY}
            stroke="#f59e0b"
            strokeWidth="1.2"
            strokeDasharray="3,3"
            opacity={0.75}
          />
          {/* Component Collar Ring */}
          <circle cx={compAnchorX} cy={compAnchorY} r={4} fill="#0f172a" stroke="#f59e0b" strokeWidth={1.5} opacity={0.9} />
          {/* Inner Dot on Component */}
          <circle cx={compAnchorX} cy={compAnchorY} r={2} fill="#f59e0b" opacity={0.95} />
        </g>
      )}

      {/* Interactive Badge */}
      <g
        transform={`translate(${badgeX}, ${badgeY})`}
        style={{
          cursor: isDesignMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
          pointerEvents: isDesignMode ? 'auto' : 'none',
          userSelect: 'none',
        }}
        onPointerDown={onPointerDown}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.();
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onReset?.();
        }}
      >
        <title>
          {isDesignMode
            ? `Komponent İsmi: ${displayName}\n• Sürükleyerek istediğiniz yere taşıyabilirsiniz\n• Çift tıklayarak orijinal yerine sıfırlayabilirsiniz`
            : `Komponent: ${displayName}`}
        </title>

        {/* Large invisible hit area for easy grabbing */}
        <rect
          x={-halfW - 4}
          y={-halfH - 4}
          width={badgeWidth + 8}
          height={badgeHeight + 8}
          fill="transparent"
        />

        {/* Badge Body */}
        <rect
          x={-halfW}
          y={-halfH}
          width={badgeWidth}
          height={badgeHeight}
          rx="4"
          fill="#090d16"
          stroke={isDragging ? '#fbbf24' : isSelected ? '#f59e0b' : isMoved ? '#d97706' : 'rgba(255, 255, 255, 0.22)'}
          strokeWidth={isDragging ? 1.8 : isSelected || isMoved ? 1.4 : 1}
          style={{
            filter: isDragging
              ? 'drop-shadow(0 0 6px rgba(245, 158, 11, 0.7))'
              : 'drop-shadow(0 2px 4px rgba(0,0,0,0.65))',
          }}
        />

        {/* Tag indicator dot */}
        <circle
          cx={-halfW + 8}
          cy={0}
          r={2.5}
          fill={isDragging ? '#fbbf24' : isSelected ? '#f59e0b' : '#94a3b8'}
        />

        {/* Subtle Grip Dots in Design Mode */}
        {isDesignMode && (
          <g transform={`translate(${halfW - 9}, -4)`} opacity={0.6}>
            <circle cx="0" cy="2" r="1" fill="#94a3b8" />
            <circle cx="0" cy="6" r="1" fill="#94a3b8" />
            <circle cx="3" cy="2" r="1" fill="#94a3b8" />
            <circle cx="3" cy="6" r="1" fill="#94a3b8" />
          </g>
        )}

        {/* Text */}
        <text
          x={isDesignMode ? 2 : 4}
          y={4}
          fill={isDragging ? '#fbbf24' : '#f1f5f9'}
          fontSize="11"
          fontWeight="600"
          textAnchor="middle"
          letterSpacing="0.3"
        >
          {displayName}
        </text>
      </g>
    </g>
  );
}

function getNameBadgeAnchor(d: Device): { cx: number; cy: number; anchorX: number; anchorY: number } {
  if (d.type === 'pump' || (d.type === 'motor' && d.props['variant'] === 'pump_motor')) {
    return { cx: 60, cy: 114, anchorX: 65, anchorY: 65 };
  }
  if (d.type === 'tank') {
    return { cx: 70, cy: 196, anchorX: 70, anchorY: 155 };
  }
  if (d.type === 'valve') {
    return { cx: 50, cy: 114, anchorX: 50, anchorY: 60 };
  }
  if (d.type === 'fan' || (d.type === 'motor' && d.props['variant'] === 'fan')) {
    return { cx: 40, cy: 94, anchorX: 40, anchorY: 55 };
  }
  if (d.type === 'mixer' || (d.type === 'motor' && d.props['variant'] === 'mixer')) {
    const isAttached = Boolean(d.props['tankId']);
    return { cx: 25, cy: isAttached ? 74 : 144, anchorX: 25, anchorY: isAttached ? 45 : 65 };
  }
  if (d.type === 'motor') {
    return { cx: 75, cy: 110, anchorX: 75, anchorY: 60 };
  }
  if (d.type === 'lamp' || d.type === 'button') {
    return { cx: 30, cy: 74, anchorX: 30, anchorY: 40 };
  }
  if (d.type === 'sensor') {
    const range = Number(d.props['range']) || 80;
    const svgH = Math.max(60, range + 44);
    return { cx: 22, cy: svgH + 14, anchorX: 22, anchorY: 30 };
  }
  if (d.type === 'level-sensor') {
    const isAttached = Boolean(d.props['tankId']);
    const svgH = isAttached ? 46 : 160;
    return { cx: 22, cy: svgH + 14, anchorX: 22, anchorY: 25 };
  }
  if (d.type === 'jam-sensor') {
    return { cx: 22, cy: 74, anchorX: 22, anchorY: 30 };
  }
  if (d.type === 'pid-controller') {
    return { cx: 50, cy: 94, anchorX: 50, anchorY: 45 };
  }
  return { cx: 30, cy: 74, anchorX: 30, anchorY: 30 };
}

export function SceneView({ viewport: viewportProp }: { viewport?: Viewport }) {
  const t = useT();
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const storeViewport = useSceneEditorStore((s) => s.viewport);
  const { zoomIn, zoomOut, resetViewport, zoomAtPoint, panByOffset } = useSceneEditorStore();
  const viewport = viewportProp ?? storeViewport;
  const cell = GRID_SIZE * viewport.zoom;

  const devices = useProjectStore((s) => s.project.scene.devices);
  const setDevices = useProjectStore((s) => s.setDevices);
  const pipes = useProjectStore((s) => s.project.scene.pipes || []);
  const addPipe = useProjectStore((s) => s.addPipe);
  const updatePipe = useProjectStore((s) => s.updatePipe);
  const removePipe = useProjectStore((s) => s.removePipe);

  // Pipe interactive manipulation state
  const [activePipeDrag, setActivePipeDrag] = useState<{
    pipeId: string;
    fromDevId: string;
    toDevId: string;
    type: 'segment' | 'corner';
    segmentIndex?: number;
    segmentOrientation?: 'horizontal' | 'vertical';
    cornerIndex?: number;
    initialRoutePoints: { x: number; y: number }[];
  } | null>(null);
  const [pipeLiveWaypoints, setPipeLiveWaypoints] = useState<{ pipeId: string; waypoints: { x: number; y: number }[] } | null>(null);

  // Component address and name badges interactive dragging state
  const [activeLabelDrag, setActiveLabelDrag] = useState<{
    deviceId: string;
    target: 'address' | 'name';
    startScenePos: { x: number; y: number };
    initialOffset: { x: number; y: number };
    liveOffset: { x: number; y: number };
  } | null>(null);

  const handleLabelPointerDown = (
    e: React.PointerEvent,
    deviceId: string,
    target: 'address' | 'name',
    currentOffsetX: number,
    currentOffsetY: number
  ) => {
    if (!isDesignMode || e.button !== 0) return;
    e.stopPropagation();
    try {
      (e.currentTarget as any).setPointerCapture(e.pointerId);
    } catch {}

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const scenePos = screenToScene(viewport, { x: e.clientX - rect.left, y: e.clientY - rect.top });

    setActiveLabelDrag({
      deviceId,
      target,
      startScenePos: scenePos,
      initialOffset: { x: currentOffsetX, y: currentOffsetY },
      liveOffset: { x: currentOffsetX, y: currentOffsetY },
    });
  };

  const handleResetLabelOffset = (deviceId: string, target: 'address' | 'name' = 'address') => {
    const newDevices = devices.map((d) => {
      if (d.id === deviceId) {
        const nextProps = { ...d.props };
        if (target === 'name') {
          delete nextProps['nameOffsetX'];
          delete nextProps['nameOffsetY'];
        } else {
          delete nextProps['labelOffsetX'];
          delete nextProps['labelOffsetY'];
        }
        return { ...d, props: nextProps };
      }
      return d;
    });
    setDevices(newDevices);
  };

  // Pipe drawing state
  const [pipeDrawingStart, setPipeDrawingStart] = useState<{ deviceId: string; portType: PipePortType; portId?: string | undefined } | null>(null);
  const [pipeDrawingWaypoints, setPipeDrawingWaypoints] = useState<{ x: number; y: number }[]>([]);
  const [cursorScenePos, setCursorScenePos] = useState<{ x: number; y: number } | null>(null);
  const [hoveredPort, setHoveredPort] = useState<{ deviceId: string; portType: PipePortType; portId?: string | undefined } | null>(null);

  const simState = useSimulationStore((s) => s.snapshot.state);
  const isRunning = simState === 'RUNNING' || simState === 'PAUSED';
  const activeMode = useUiStore((s) => s.activeMode);
  const isDesignMode = activeMode === 'design';
  const deviceRuntimeStates = useSimulationStore((s) => s.snapshot.devices || {});
  const products = useSimulationStore((s) => s.snapshot.products || []);
  const processEngine = useSimulationStore((s) => s.engine.process);
  const hasJammed = products.some((p: any) => p.active && (p.blocked || (p.blockedTimeMs ?? 0) > 0)) || Object.values(deviceRuntimeStates).some((st: any) => Boolean(st?.jammed));

  const select = useUiStore((s) => s.select);
  const selectedObjectId = useUiStore((s) => s.selectedObjectId);
  const hasHMI = useProjectStore((s) => s.project.hmi?.screens?.some((sc) => sc.components.length > 0) ?? false);
  const hmiOverlayOpen = useUiStore((s) => s.hmiOverlayOpen);
  const toggleHmiOverlay = useUiStore((s) => s.toggleHmiOverlay);

  const [dragOver, setDragOver] = useState(false);
  const [draggingDevice, setDraggingDevice] = useState<Device | null>(null);

  useEffect(() => {
    if (isRunning) {
      setDraggingDevice(null);
      setPipeDrawingStart(null);
      setPipeDrawingWaypoints([]);
      setCursorScenePos(null);
      setHoveredPort(null);
      setDragOver(false);
    }
  }, [isRunning]);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Pan state
  const [isPanning, setIsPanning] = useState(false);
  const lastMouseRef = useRef({ x: 0, y: 0 });
  const isSpacePressedRef = useRef(false);
  const isMouseDownRef = useRef(false);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const hasMovedRef = useRef(false);

  const handleZoomIn = useCallback(() => {
    const rect = svgRef.current?.getBoundingClientRect();
    const anchor = rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 400, y: 300 };
    zoomIn(anchor);
  }, [zoomIn]);

  const handleZoomOut = useCallback(() => {
    const rect = svgRef.current?.getBoundingClientRect();
    const anchor = rect ? { x: rect.width / 2, y: rect.height / 2 } : { x: 400, y: 300 };
    zoomOut(anchor);
  }, [zoomOut]);

  const handleResetZoom = useCallback(() => {
    resetViewport();
  }, [resetViewport]);

  useEffect(() => {
    const handleWindowKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input field
      if (document.activeElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)) {
        return;
      }

      if (e.code === 'Space') {
        isSpacePressedRef.current = true;
        if (containerRef.current) containerRef.current.style.cursor = 'grab';
      }

      if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-' || e.key === '_') {
        handleZoomOut();
      } else if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleResetZoom();
      }

      if (e.key === 'Escape') {
        setPipeDrawingStart(null);
        setPipeDrawingWaypoints([]);
      }

      if (!isRunning && pipeDrawingStart && (e.key === 'Backspace' || e.key === 'Delete')) {
        if (pipeDrawingWaypoints.length > 0) {
          e.preventDefault();
          setPipeDrawingWaypoints((prev) => prev.slice(0, -1));
          return;
        }
      }

      if (isDesignMode && !isRunning && selectedObjectId && (e.key === 'Delete' || e.key === 'Backspace')) {
        const selectedDevice = devices.find((d) => d.id === selectedObjectId);
        if (selectedDevice) {
          const newDevices = devices.filter((d) => d.id !== selectedObjectId);
          setDevices(newDevices);
          useUiStore.getState().clearSelection();
        } else if (selectedObjectId.startsWith('pipe_')) {
          removePipe(selectedObjectId);
          useUiStore.getState().clearSelection();
        }
      }
    };

    const handleWindowKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        isSpacePressedRef.current = false;
        if (containerRef.current) containerRef.current.style.cursor = 'default';
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleWindowKeyDown);
    window.addEventListener('keyup', handleWindowKeyUp);
    return () => {
      window.removeEventListener('keydown', handleWindowKeyDown);
      window.removeEventListener('keyup', handleWindowKeyUp);
    };
  }, [isDesignMode, selectedObjectId, devices, setDevices, removePipe, handleZoomIn, handleZoomOut, handleResetZoom]);

  // Prevent default page scroll on Ctrl+wheel
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const preventWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };
    el.addEventListener('wheel', preventWheel, { passive: false });
    return () => el.removeEventListener('wheel', preventWheel);
  }, []);

  const handleWheel = (e: React.WheelEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const anchor = { x: e.clientX - rect.left, y: e.clientY - rect.top };

    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomAtPoint(factor, anchor);
    } else {
      if (e.shiftKey) {
        panByOffset(-e.deltaY, 0);
      } else {
        panByOffset(-e.deltaX, -e.deltaY);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (isDesignMode && !isRunning && e.dataTransfer.types.includes('application/x-plc-device')) {
      e.dataTransfer.dropEffect = 'copy';
      setDragOver(true);
    } else {
      e.dataTransfer.dropEffect = 'none';
      setDragOver(false);
    }
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (!isDesignMode || isRunning) return;
    const typeStr = e.dataTransfer.getData('application/x-plc-device');
    if (!typeStr) return;
    const type = typeStr as DeviceType;

    const def = deviceRegistry.get(type);
    if (!def) return;

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Convert drop coordinates to scene and snap to grid
    const scenePos = screenToScene(viewport, { x: e.clientX - rect.left, y: e.clientY - rect.top });
    let snapped = snapPointToGrid(scenePos, GRID_SIZE);
    let initialTankId: string | undefined = undefined;

    if (type === 'mixer') {
      const tanks = devices.filter((dev) => dev.type === 'tank');
      for (const tank of tanks) {
        const tx = tank.transform?.position?.x ?? 0;
        const ty = tank.transform?.position?.y ?? 0;
        const mountX = tx + 40;
        const mountY = ty - 48;
        if (Math.hypot(snapped.x - mountX, snapped.y - mountY) < 50) {
          snapped = { x: mountX, y: mountY };
          initialTankId = tank.id;
          break;
        }
      }
    }

    if (type === 'level-sensor') {
      const tanks = devices.filter((dev) => dev.type === 'tank');
      for (const tank of tanks) {
        const tx = tank.transform?.position?.x ?? 0;
        const ty = tank.transform?.position?.y ?? 0;
        const mountX = tx + 72;
        const mountY = ty - 18;
        if (Math.hypot(snapped.x - mountX, snapped.y - mountY) < 50) {
          snapped = { x: mountX, y: mountY };
          initialTankId = tank.id;
          break;
        }
      }
    }

    const localizedTypeName = t(def.meta.nameKey) || type;
    const count = devices.filter((dev) => dev.type === type).length + 1;
    const defaultName = `${localizedTypeName} ${count}`;

    const newDevice: Device = {
      id: `${type}-${Date.now()}` as DeviceId,
      type: type,
      name: defaultName,
      transform: { position: snapped, rotation: 0 },
      props: { ...def.defaultProps, ...(initialTankId ? { tankId: initialTankId } : {}) },
    };

    setDevices([...devices, newDevice]);
    select(newDevice.id, type);
  };

  const handleDeviceClick = (e: React.MouseEvent, d: Device) => {
    e.stopPropagation();
    if (isRunning) {
      if (d.type === 'emergency-stop') {
        const dev = processEngine.getDevice(d.id);
        if (dev) {
          dev.state.triggered = !dev.state.triggered;
        }
      }
    } else if (isDesignMode) {
      select(d.id, d.type);
    }
  };

  const handleDeviceMouseDown = (e: React.MouseEvent, d: Device) => {
    if (isRunning) {
      if (d.type === 'button') {
        const dev = processEngine.getDevice(d.id);
        if (dev) dev.state.pressed = true;
      }
    } else if (isDesignMode) {
      e.stopPropagation();
      select(d.id, d.type);
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const scenePos = screenToScene(viewport, { x: e.clientX - rect.left, y: e.clientY - rect.top });
        const posX = d.transform?.position?.x ?? 0;
        const posY = d.transform?.position?.y ?? 0;
        setDragOffset({ x: scenePos.x - posX, y: scenePos.y - posY });
        setDraggingDevice(d);
      }
    }
  };

  const handleDeviceMouseUp = (d: Device) => {
    if (isRunning && d.type === 'button') {
      const dev = processEngine.getDevice(d.id);
      if (dev) dev.state.pressed = false;
    }
  };

  const handlePortClick = (e: React.MouseEvent, deviceId: string, portType: PipePortType, portId?: string) => {
    e.stopPropagation();
    if (!isDesignMode || isRunning) return;

    if (!pipeDrawingStart) {
      setPipeDrawingStart({ deviceId, portType, portId });
      setPipeDrawingWaypoints([]);
    } else {
      if (
        pipeDrawingStart.deviceId === deviceId &&
        (pipeDrawingStart.portId && portId ? pipeDrawingStart.portId === portId : pipeDrawingStart.portType === portType)
      ) {
        setPipeDrawingStart(null);
        setPipeDrawingWaypoints([]);
        return;
      }

      const newPipe: PipeConnection = {
        id: `pipe_${Date.now()}`,
        from: pipeDrawingStart,
        to: { deviceId, portType, portId },
        ...(pipeDrawingWaypoints.length > 0 ? { waypoints: pipeDrawingWaypoints } : {}),
      };
      addPipe(newPipe);
      setPipeDrawingStart(null);
      setPipeDrawingWaypoints([]);
      select(newPipe.id, 'pipe' as any);
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.button === 1 || isSpacePressedRef.current) {
      setIsPanning(true);
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
      e.currentTarget.setPointerCapture(e.pointerId);
      if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
      return;
    }

    if (e.button === 0 && !draggingDevice) {
      const targetTag = (e.target as Element).tagName.toLowerCase();
      const isBg = targetTag === 'svg' || (targetTag === 'rect' && (e.target as Element).getAttribute('fill')?.includes(PATTERN_ID));
      if (isBg) {
        isMouseDownRef.current = true;
        hasMovedRef.current = false;
        dragStartPosRef.current = { x: e.clientX, y: e.clientY };
        lastMouseRef.current = { x: e.clientX, y: e.clientY };
      }
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (activeLabelDrag && isDesignMode && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const scenePos = screenToScene(viewport, { x: e.clientX - rect.left, y: e.clientY - rect.top });
      const dx = scenePos.x - activeLabelDrag.startScenePos.x;
      const dy = scenePos.y - activeLabelDrag.startScenePos.y;

      const liveX = Math.round((activeLabelDrag.initialOffset.x + dx) / 4) * 4;
      const liveY = Math.round((activeLabelDrag.initialOffset.y + dy) / 4) * 4;

      setActiveLabelDrag((prev) =>
        prev ? { ...prev, liveOffset: { x: liveX, y: liveY } } : null
      );
      return;
    }

    if (activePipeDrag && isDesignMode && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const scenePos = screenToScene(viewport, { x: e.clientX - rect.left, y: e.clientY - rect.top });
      const snapped = snapPointToGrid(scenePos, GRID_SIZE);

      const fromDev = devices.find((d) => d.id === activePipeDrag.fromDevId);
      const toDev = devices.find((d) => d.id === activePipeDrag.toDevId);
      const pipe = pipes.find((p) => p.id === activePipeDrag.pipeId);

      if (fromDev && toDev && pipe) {
        const p1 = getPortWorldPosition(fromDev, pipe.from.portType, pipe.from.portId);
        const p2 = getPortWorldPosition(toDev, pipe.to.portType, pipe.to.portId);
        if (p1 && p2) {
          const newWaypoints = calculateDraggedPipeWaypoints(
            p1,
            p1.dir,
            p2,
            p2.dir,
            activePipeDrag.initialRoutePoints,
            activePipeDrag.type,
            activePipeDrag.segmentIndex,
            activePipeDrag.segmentOrientation,
            activePipeDrag.cornerIndex,
            snapped
          );
          setPipeLiveWaypoints({ pipeId: activePipeDrag.pipeId, waypoints: newWaypoints });
        }
      }
      return;
    }

    if (pipeDrawingStart && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect();
      const scenePos = screenToScene(viewport, { x: e.clientX - rect.left, y: e.clientY - rect.top });
      setCursorScenePos(scenePos);
    }

    if (isPanning) {
      const dx = e.clientX - lastMouseRef.current.x;
      const dy = e.clientY - lastMouseRef.current.y;
      panByOffset(dx, dy);
      lastMouseRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    if (isMouseDownRef.current && !draggingDevice) {
      const dist = Math.hypot(e.clientX - dragStartPosRef.current.x, e.clientY - dragStartPosRef.current.y);
      if (dist > 3) {
        hasMovedRef.current = true;
        setIsPanning(true);
        lastMouseRef.current = { x: e.clientX, y: e.clientY };
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // pointer capture might fail in tests
        }
        if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
      }
      return;
    }

    if (draggingDevice && isDesignMode) {
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const scenePos = screenToScene(viewport, { x: e.clientX - rect.left, y: e.clientY - rect.top });
        const targetPos = { x: scenePos.x - dragOffset.x, y: scenePos.y - dragOffset.y };
        let snapped = snapPointToGrid(targetPos, GRID_SIZE);

        const isMixer = draggingDevice.type === 'mixer' || (draggingDevice.type === 'motor' && draggingDevice.props['variant'] === 'mixer');
        const isLevelSensor = draggingDevice.type === 'level-sensor';
        let newTankId = draggingDevice.props['tankId'] as string | undefined;

        if (isMixer) {
          const tanks = devices.filter((dev) => dev.type === 'tank' && dev.id !== draggingDevice.id);
          let closestTank: Device | null = null;
          let minDist = 45;

          for (const tank of tanks) {
            const tx = tank.transform?.position?.x ?? 0;
            const ty = tank.transform?.position?.y ?? 0;
            const mountX = tx + 40;
            const mountY = ty - 48;
            const dist = Math.hypot(targetPos.x - mountX, targetPos.y - mountY);
            if (dist < minDist) {
              minDist = dist;
              closestTank = tank;
            }
          }

          if (closestTank) {
            const tx = closestTank.transform?.position?.x ?? 0;
            const ty = closestTank.transform?.position?.y ?? 0;
            snapped = { x: tx + 40, y: ty - 48 };
            newTankId = closestTank.id;
          } else {
            newTankId = undefined;
          }
        }

        if (isLevelSensor) {
          const tanks = devices.filter((dev) => dev.type === 'tank' && dev.id !== draggingDevice.id);
          let closestTank: Device | null = null;
          let minDist = 45;

          for (const tank of tanks) {
            const tx = tank.transform?.position?.x ?? 0;
            const ty = tank.transform?.position?.y ?? 0;
            const mountX = tx + 72;
            const mountY = ty - 18;
            const dist = Math.hypot(targetPos.x - mountX, targetPos.y - mountY);
            if (dist < minDist) {
              minDist = dist;
              closestTank = tank;
            }
          }

          if (closestTank) {
            const tx = closestTank.transform?.position?.x ?? 0;
            const ty = closestTank.transform?.position?.y ?? 0;
            snapped = { x: tx + 72, y: ty - 18 };
            newTankId = closestTank.id;
          } else {
            newTankId = undefined;
          }
        }

        const isTank = draggingDevice.type === 'tank';

        const newDevices = devices.map((dev) => {
          if (dev.id === draggingDevice.id) {
            let nextProps = dev.props;
            if (isMixer || isLevelSensor) {
              const updatedProps = { ...dev.props };
              if (newTankId) {
                updatedProps['tankId'] = newTankId;
              } else {
                delete updatedProps['tankId'];
              }
              nextProps = updatedProps;
            }
            return {
              ...dev,
              transform: { ...dev.transform, position: snapped },
              props: nextProps,
            };
          }
          if (isTank) {
            const attachedMixer = (dev.type === 'mixer' || (dev.type === 'motor' && dev.props['variant'] === 'mixer')) && dev.props['tankId'] === draggingDevice.id;
            if (attachedMixer) {
              return {
                ...dev,
                transform: { ...dev.transform, position: { x: snapped.x + 40, y: snapped.y - 48 } },
              };
            }
            const attachedSensor = dev.type === 'level-sensor' && dev.props['tankId'] === draggingDevice.id;
            if (attachedSensor) {
              return {
                ...dev,
                transform: { ...dev.transform, position: { x: snapped.x + 72, y: snapped.y - 18 } },
              };
            }
          }
          return dev;
        });
        setDevices(newDevices);
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (activeLabelDrag) {
      try {
        (e.currentTarget as any).releasePointerCapture(e.pointerId);
      } catch {}

      const { deviceId, target, liveOffset } = activeLabelDrag;
      const newDevices = devices.map((d) => {
        if (d.id === deviceId) {
          const offsetXKey = target === 'name' ? 'nameOffsetX' : 'labelOffsetX';
          const offsetYKey = target === 'name' ? 'nameOffsetY' : 'labelOffsetY';
          return {
            ...d,
            props: {
              ...d.props,
              [offsetXKey]: liveOffset.x,
              [offsetYKey]: liveOffset.y,
            },
          };
        }
        return d;
      });
      setDevices(newDevices);
      setActiveLabelDrag(null);
      isMouseDownRef.current = false;
      hasMovedRef.current = false;
      return;
    }

    if (activePipeDrag) {
      if (pipeLiveWaypoints && pipeLiveWaypoints.pipeId === activePipeDrag.pipeId) {
        updatePipe(activePipeDrag.pipeId, { waypoints: pipeLiveWaypoints.waypoints });
      }
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      setActivePipeDrag(null);
      setPipeLiveWaypoints(null);
      isMouseDownRef.current = false;
      hasMovedRef.current = false;
      return;
    }

    if (isPanning) {
      setIsPanning(false);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      if (containerRef.current) {
        containerRef.current.style.cursor = isSpacePressedRef.current ? 'grab' : 'default';
      }
    }

    if (isMouseDownRef.current && !hasMovedRef.current && !draggingDevice && !isRunning) {
      if (pipeDrawingStart && svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        const scenePos = screenToScene(viewport, { x: e.clientX - rect.left, y: e.clientY - rect.top });
        const snapped = snapPointToGrid(scenePos, GRID_SIZE);

        setPipeDrawingWaypoints((prev) => {
          const last = prev[prev.length - 1];
          if (last && Math.abs(last.x - snapped.x) < 4 && Math.abs(last.y - snapped.y) < 4) {
            return prev;
          }
          return [...prev, snapped];
        });
      } else {
        useUiStore.getState().clearSelection();
      }
    }

    isMouseDownRef.current = false;
    hasMovedRef.current = false;
    setDraggingDevice(null);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (pipeDrawingStart) {
      e.preventDefault();
      setPipeDrawingStart(null);
      setPipeDrawingWaypoints([]);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    const targetTag = (e.target as Element).tagName.toLowerCase();
    const isBg = targetTag === 'svg' || (targetTag === 'rect' && (e.target as Element).getAttribute('fill')?.includes(PATTERN_ID));
    if (isBg) {
      resetViewport();
    }
  };

  return (
    <div
      ref={containerRef}
      role="region"
      aria-label={t('scene.label')}
      className={styles.scene}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onContextMenu={handleContextMenu}
      onDoubleClick={handleDoubleClick}
      style={{
        backgroundColor: dragOver ? 'var(--color-bg-hover)' : undefined,
        cursor: isPanning ? 'grabbing' : isSpacePressedRef.current ? 'grab' : 'default',
      }}
    >
      <svg className={styles.grid} aria-hidden="true" focusable="false" ref={svgRef}>
        <defs>
          <pattern
            id={PATTERN_ID}
            width={cell}
            height={cell}
            x={viewport.panX}
            y={viewport.panY}
            patternUnits="userSpaceOnUse"
          >
            <circle cx={1} cy={1} r={1.3} className={styles.dot} />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${PATTERN_ID})`} />

        <g transform={`translate(${viewport.panX}, ${viewport.panY}) scale(${viewport.zoom})`}>

        {/* Render Connected 3D Pipes */}
        {pipes.map((pipe) => {
          const isPipeSelected = selectedObjectId === pipe.id;
          const fromDev = devices.find((d) => d.id === pipe.from.deviceId);
          const toDev = devices.find((d) => d.id === pipe.to.deviceId);
          if (!fromDev || !toDev) return null;

          const p1 = getPortWorldPosition(fromDev, pipe.from.portType, pipe.from.portId);
          const p2 = getPortWorldPosition(toDev, pipe.to.portType, pipe.to.portId);
          if (!p1 || !p2) return null;

          const effectiveWaypoints = pipeLiveWaypoints?.pipeId === pipe.id ? pipeLiveWaypoints.waypoints : pipe.waypoints;
          const route = computePipeRoute(p1, p1.dir, p2, p2.dir, 10, effectiveWaypoints);

          // Flow physics animation check
          const isSimActive = (simState === 'RUNNING' || simState === 'PAUSED') && activeMode !== 'design';
          const fromState = deviceRuntimeStates[fromDev.id] || {};
          const toState = deviceRuntimeStates[toDev.id] || {};
          const isFlowing =
            isSimActive &&
            (Boolean(fromState.running) ||
              Boolean(toState.running) ||
              Number(fromState.actualFlow) > 0 ||
              Number(toState.actualFlow) > 0);

          const tankDev = fromDev.type === 'tank' ? fromDev : (toDev.type === 'tank' ? toDev : null);
          const liquidColor = tankDev?.props['color']?.toString() || '#0284c7';

          return (
            <g key={pipe.id}>
              <PipeSvg
                pathData={route.pathData}
                elbows={route.elbows}
                isFlowing={isFlowing}
                liquidColor={liquidColor}
                pipeColor={pipe.color}
                diameter={pipe.diameter || 8}
                isSelected={isPipeSelected}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDesignMode && !isRunning) {
                    select(pipe.id, 'pipe' as any);
                  }
                }}
                onDelete={() => {
                  if (isRunning) return;
                  removePipe(pipe.id);
                  useUiStore.getState().clearSelection();
                }}
              />

              {/* Interactive Segment and Corner Handles for Selected Pipe in Design Mode */}
              {isDesignMode && !isRunning && isPipeSelected && (
                <g className="pipe-interactive-handles">
                  {/* Segment Drag Handles (Horizontal / Vertical line adjusters) */}
                  {route.points.map((ptA, i) => {
                    const ptB = route.points[i + 1];
                    if (!ptB) return null;

                    const isH = Math.abs(ptA.y - ptB.y) < 1;
                    const isV = Math.abs(ptA.x - ptB.x) < 1;
                    if (!isH && !isV) return null;

                    const segLen = isH ? Math.abs(ptB.x - ptA.x) : Math.abs(ptB.y - ptA.y);
                    if (segLen < 14) return null;

                    const midX = (ptA.x + ptB.x) / 2;
                    const midY = (ptA.y + ptB.y) / 2;

                    if (isH) {
                      return (
                        <g
                          key={`pipe-seg-h-${pipe.id}-${i}`}
                          transform={`translate(${midX}, ${midY})`}
                          style={{ cursor: 'ns-resize' }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            try { (e.currentTarget as any).setPointerCapture(e.pointerId); } catch {}
                            setActivePipeDrag({
                              pipeId: pipe.id,
                              fromDevId: fromDev.id,
                              toDevId: toDev.id,
                              type: 'segment',
                              segmentIndex: i,
                              segmentOrientation: 'horizontal',
                              initialRoutePoints: route.points,
                            });
                          }}
                        >
                          <title>Boru Hattını Yukarı / Aşağı Kaydır</title>
                          {/* Large invisible hit area */}
                          <rect x="-14" y="-12" width="28" height="24" fill="transparent" />
                          {/* Pill body */}
                          <rect
                            x="-12"
                            y="-5"
                            width="24"
                            height="10"
                            rx="5"
                            fill="#0284c7"
                            stroke="#ffffff"
                            strokeWidth="1.2"
                            style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}
                          />
                          {/* Up/Down arrow ↕ */}
                          <line x1="0" y1="-3" x2="0" y2="3" stroke="#ffffff" strokeWidth="1.2" strokeLinecap="round" />
                          <path d="M 0 -4.2 L -2.2 -1.8 L 2.2 -1.8 Z" fill="#ffffff" />
                          <path d="M 0 4.2 L -2.2 1.8 L 2.2 1.8 Z" fill="#ffffff" />
                        </g>
                      );
                    } else {
                      return (
                        <g
                          key={`pipe-seg-v-${pipe.id}-${i}`}
                          transform={`translate(${midX}, ${midY})`}
                          style={{ cursor: 'ew-resize' }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            try { (e.currentTarget as any).setPointerCapture(e.pointerId); } catch {}
                            setActivePipeDrag({
                              pipeId: pipe.id,
                              fromDevId: fromDev.id,
                              toDevId: toDev.id,
                              type: 'segment',
                              segmentIndex: i,
                              segmentOrientation: 'vertical',
                              initialRoutePoints: route.points,
                            });
                          }}
                        >
                          <title>Boru Hattını Sağa / Sola Kaydır</title>
                          {/* Large invisible hit area */}
                          <rect x="-12" y="-14" width="24" height="28" fill="transparent" />
                          {/* Pill body */}
                          <rect
                            x="-5"
                            y="-12"
                            width="10"
                            height="24"
                            rx="5"
                            fill="#0284c7"
                            stroke="#ffffff"
                            strokeWidth="1.2"
                            style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}
                          />
                          {/* Left/Right arrow ↔ */}
                          <line x1="-3" y1="0" x2="3" y2="0" stroke="#ffffff" strokeWidth="1.2" strokeLinecap="round" />
                          <path d="M -4.2 0 L -1.8 -2.2 L -1.8 2.2 Z" fill="#ffffff" />
                          <path d="M 4.2 0 L 1.8 -2.2 L 1.8 2.2 Z" fill="#ffffff" />
                        </g>
                      );
                    }
                  })}

                  {/* Corner / Elbow handles */}
                  {route.points.map((pt, i) => {
                    if (i === 0 || i === route.points.length - 1) return null;
                    return (
                      <g
                        key={`pipe-corner-${pipe.id}-${i}`}
                        transform={`translate(${pt.x}, ${pt.y})`}
                        style={{ cursor: 'move' }}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          try { (e.currentTarget as any).setPointerCapture(e.pointerId); } catch {}
                          setActivePipeDrag({
                            pipeId: pipe.id,
                            fromDevId: fromDev.id,
                            toDevId: toDev.id,
                            type: 'corner',
                            cornerIndex: i,
                            initialRoutePoints: route.points,
                          });
                        }}
                      >
                        <title>Dirsek Konumunu Taşı</title>
                        <circle r="14" fill="transparent" />
                        <circle r="6" fill="#0284c7" stroke="#ffffff" strokeWidth="1.8" style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }} />
                        <circle r="2" fill="#ffffff" />
                      </g>
                    );
                  })}

                  {/* Quick Action Floating Bar next to selected pipe */}
                  {(() => {
                    const midPt = route.points[Math.floor(route.points.length / 2)] || { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
                    const hasCustomWaypoints = Boolean(pipe.waypoints && pipe.waypoints.length > 0);
                    return (
                      <g transform={`translate(${midPt.x}, ${midPt.y - 20})`} style={{ pointerEvents: 'auto' }}>
                        <rect
                          x={hasCustomWaypoints ? -68 : -30}
                          y="-12"
                          width={hasCustomWaypoints ? 136 : 60}
                          height="22"
                          rx="5"
                          fill="#0f172a"
                          stroke="#334155"
                          strokeWidth="1"
                          style={{ filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.6))' }}
                        />
                        {hasCustomWaypoints && (
                          <g
                            onClick={(e) => {
                              e.stopPropagation();
                              updatePipe(pipe.id, { waypoints: [] });
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <text x="-34" y="2" fill="#38bdf8" fontSize="10" fontWeight="bold" textAnchor="middle">
                              ↺ Sıfırla
                            </text>
                            <line x1="0" y1="-8" x2="0" y2="6" stroke="#334155" strokeWidth="1" />
                          </g>
                        )}
                        <g
                          onClick={(e) => {
                            e.stopPropagation();
                            removePipe(pipe.id);
                            useUiStore.getState().clearSelection();
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <text x={hasCustomWaypoints ? 34 : 0} y="2" fill="#f87171" fontSize="10" fontWeight="bold" textAnchor="middle">
                            ✕ Sil
                          </text>
                        </g>
                      </g>
                    );
                  })()}
                </g>
              )}
            </g>
          );
        })}

        {/* Render Preview Pipe while drawing */}
        {pipeDrawingStart && (() => {
          const startDev = devices.find((d) => d.id === pipeDrawingStart.deviceId);
          if (!startDev) return null;
          const p1 = getPortWorldPosition(startDev, pipeDrawingStart.portType, pipeDrawingStart.portId);
          if (!p1) return null;

          let p2: { x: number; y: number } | null = null;
          let dir2: PortDirection = 'down';

          if (hoveredPort) {
            const targetDev = devices.find((d) => d.id === hoveredPort.deviceId);
            if (targetDev) {
              const targetP = getPortWorldPosition(targetDev, hoveredPort.portType, hoveredPort.portId);
              if (targetP) {
                p2 = { x: targetP.x, y: targetP.y };
                dir2 = targetP.dir;
              }
            }
          }

          if (!p2 && cursorScenePos) {
            p2 = snapPointToGrid(cursorScenePos, GRID_SIZE);
            const refPoint = pipeDrawingWaypoints.length > 0 ? pipeDrawingWaypoints[pipeDrawingWaypoints.length - 1]! : p1;
            dir2 =
              Math.abs(p2.x - refPoint.x) > Math.abs(p2.y - refPoint.y)
                ? (p2.x > refPoint.x ? 'left' : 'right')
                : (p2.y > refPoint.y ? 'up' : 'down');
          }

          if (!p2) return null;

          const previewRoute = computePipeRoute(p1, p1.dir, p2, dir2, 10, pipeDrawingWaypoints);
          return (
            <g className="pipe-preview-container">
              <PipeSvg
                pathData={previewRoute.pathData}
                elbows={previewRoute.elbows}
                isDraft={true}
                diameter={8}
              />
              {/* Waypoint markers placed so far */}
              {pipeDrawingWaypoints.map((wp, idx) => (
                <g key={`preview-wp-marker-${idx}`} transform={`translate(${wp.x}, ${wp.y})`}>
                  <circle r={8} fill="#0284c7" fillOpacity={0.4} stroke="#38bdf8" strokeWidth={2} />
                  <circle r={2.5} fill="#ffffff" />
                  <text
                    x={12}
                    y={-10}
                    fill="#38bdf8"
                    fontSize={11}
                    fontWeight="bold"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                  >
                    Dirsek {idx + 1}
                  </text>
                </g>
              ))}
              {/* Snapped cursor crosshair indicator */}
              {cursorScenePos && !hoveredPort && (
                <g transform={`translate(${p2.x}, ${p2.y})`} style={{ pointerEvents: 'none' }}>
                  <circle r={6} fill="none" stroke="#38bdf8" strokeWidth={1.5} strokeDasharray="3,2" />
                  <line x1={-9} y1={0} x2={9} y2={0} stroke="#38bdf8" strokeWidth={1} opacity={0.6} />
                  <line x1={0} y1={-9} x2={0} y2={9} stroke="#38bdf8" strokeWidth={1} opacity={0.6} />
                </g>
              )}
            </g>
          );
        })()}

        {/* Render Devices */}
        {devices.map(d => {
          const isSelected = !isRunning && selectedObjectId === d.id;
          const isSimActive = (simState === 'RUNNING' || simState === 'PAUSED') && activeMode !== 'design';
          const rawState = deviceRuntimeStates[d.id] || {};
          const runtimeState = isSimActive
            ? rawState
            : {
                ...rawState,
                running: false,
                on: false,
                open: false,
                pressed: false,
                speed: 0,
                actualFlow: 0,
                detected: false,
                jammed: false,
              };

          if (d.type === 'conveyor') {
            const convL = Number(d.props['length'] || 400);
            const convW = Number(d.props['width'] || 60);
            const isRunning = Boolean(runtimeState.running);
            const isJammed = Boolean(runtimeState.jammed);
            const speed = Number(d.props['speed'] || 100);
            const direction = (d.props['direction']?.toString() as any) || 'RIGHT';
            const color = d.props['color']?.toString() || '#334155';
            const svgH = 85;

            return (
              <g
                key={d.id}
                transform={`translate(${d.transform?.position?.x ?? 0}, ${d.transform?.position?.y ?? 0})`}
                onClick={(e) => handleDeviceClick(e, d)}
                onMouseDown={(e) => handleDeviceMouseDown(e, d)}
                style={{ cursor: isRunning ? 'pointer' : (draggingDevice?.id === d.id ? 'grabbing' : 'grab') }}
              >
                <ConveyorSvg
                  isRunning={isRunning}
                  isJammed={isJammed}
                  length={convL}
                  width={convW}
                  speed={speed}
                  direction={direction}
                  color={color}
                />
                {isSelected && (
                  <rect
                    width={convL}
                    height={svgH}
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeWidth="3"
                    rx="8"
                  />
                )}
                <AddressBadge
                  address={d.props['address']?.toString()}
                  cx={convL / 2}
                  anchorX={convL / 2}
                  anchorY={25}
                  offsetX={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'address' ? activeLabelDrag.liveOffset.x : Number(d.props['labelOffsetX']) || 0}
                  offsetY={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'address' ? activeLabelDrag.liveOffset.y : Number(d.props['labelOffsetY']) || 0}
                  visible={d.props['showLabel'] !== false}
                  isDesignMode={isDesignMode}
                  isSelected={isSelected}
                  isDragging={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'address'}
                  onPointerDown={(e) =>
                    handleLabelPointerDown(
                      e,
                      d.id,
                      'address',
                      Number(d.props['labelOffsetX']) || 0,
                      Number(d.props['labelOffsetY']) || 0
                    )
                  }
                  onReset={() => handleResetLabelOffset(d.id, 'address')}
                  onSelect={() => select(d.id, d.type)}
                />
                <NameBadge
                  name={d.name}
                  cx={convL / 2}
                  cy={48}
                  anchorX={convL / 2}
                  anchorY={25}
                  offsetX={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'name' ? activeLabelDrag.liveOffset.x : Number(d.props['nameOffsetX']) || 0}
                  offsetY={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'name' ? activeLabelDrag.liveOffset.y : Number(d.props['nameOffsetY']) || 0}
                  visible={d.props['showNameLabel'] !== false}
                  isDesignMode={isDesignMode}
                  isSelected={isSelected}
                  isDragging={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'name'}
                  onPointerDown={(e) =>
                    handleLabelPointerDown(
                      e,
                      d.id,
                      'name',
                      Number(d.props['nameOffsetX']) || 0,
                      Number(d.props['nameOffsetY']) || 0
                    )
                  }
                  onReset={() => handleResetLabelOffset(d.id, 'name')}
                  onSelect={() => select(d.id, d.type)}
                />
              </g>
            );
          }

          let color = 'var(--color-text-secondary)';
          let text = d.type as string;

          if (d.type === 'motor') {
            if (runtimeState.load === 'OVERLOAD') {
              color = 'var(--color-danger)';
              text = 'M (OVERLOAD)';
            } else {
              color = runtimeState.running ? 'var(--color-success)' : 'var(--color-text-secondary)';
              text = runtimeState.running ? 'M (RUN)' : 'M (OFF)';
            }
          } else if (d.type === 'fan') {
            if (runtimeState.load === 'OVERLOAD') {
              color = 'var(--color-danger)';
              text = 'FAN (OVERLOAD)';
            } else {
              color = runtimeState.running ? 'var(--color-success)' : 'var(--color-text-secondary)';
              text = runtimeState.running ? 'FAN (RUN)' : 'FAN (OFF)';
            }
          } else if (d.type === 'mixer') {
            if (runtimeState.load === 'OVERLOAD') {
              color = 'var(--color-danger)';
              text = 'MIXER (OVERLOAD)';
            } else {
              color = runtimeState.running ? 'var(--color-success)' : 'var(--color-text-secondary)';
              text = runtimeState.running ? 'MIXER (RUN)' : 'MIXER (OFF)';
            }
          } else if (d.type === 'lamp') {
            color = runtimeState.on ? 'var(--color-warning)' : 'var(--color-text-secondary)';
            text = runtimeState.on ? 'L (ON)' : 'L (OFF)';
          } else if (d.type === 'button') {
            color = runtimeState.pressed ? 'var(--color-primary)' : 'var(--color-text-secondary)';
            text = 'BTN';
          } else if (d.type === 'emergency-stop') {
            color = runtimeState.triggered ? 'var(--color-danger)' : 'var(--color-text-secondary)';
            text = runtimeState.triggered ? 'E-STOP (TRIPPED)' : 'E-STOP (ARMED)';
          } else if (d.type === 'sensor') {
            color = runtimeState.detected ? 'var(--color-danger)' : 'var(--color-text-secondary)';
            text = 'SENS';
          } else if (d.type === 'jam-sensor') {
            color = runtimeState.detected ? 'var(--color-danger)' : 'var(--color-text-secondary)';
            text = 'JAM SENS';
          } else if (d.type === 'valve') {
            color = runtimeState.open ? 'var(--color-success)' : 'var(--color-text-secondary)';
            text = runtimeState.open ? 'VALVE (OPEN)' : 'VALVE (CLOSED)';
          } else if (d.type === 'pid-controller') {
            color = 'var(--color-primary)';
            text = 'PID';
          }

          return (
            <g
              key={d.id}
              transform={`translate(${d.transform?.position?.x ?? 0}, ${d.transform?.position?.y ?? 0})`}
              onClick={(e) => handleDeviceClick(e, d)}
              onMouseDown={(e) => handleDeviceMouseDown(e, d)}
              onMouseUp={() => handleDeviceMouseUp(d)}
              onMouseLeave={() => handleDeviceMouseUp(d)}
              style={{ cursor: isRunning ? 'pointer' : (draggingDevice?.id === d.id ? 'grabbing' : 'grab') }}
            >
              {d.type === 'lamp' ? (
                <svg width="60" height="60" viewBox="0 0 100 100">
                  <PilotLampSvg isOn={Boolean(runtimeState.on)} color={d.props['color']?.toString() || 'var(--color-warning)'} />
                  {isSelected && <rect width="100" height="100" fill="none" stroke="var(--color-primary)" strokeWidth="4" rx="8" />}
                </svg>
              ) : d.type === 'button' ? (
                <svg width="60" height="60" viewBox="0 0 120 120">
                  <PushButtonSvg isPressed={Boolean(runtimeState.pressed)} color={d.props['color']?.toString() || 'var(--color-success)'} label={d.props['label']?.toString() || ''} />
                  {isSelected && <rect width="120" height="120" fill="none" stroke="var(--color-primary)" strokeWidth="6" rx="8" />}
                </svg>
              ) : d.type === 'emergency-stop' ? (
                <svg width="60" height="60" viewBox="0 0 120 120">
                  <EmergencyStopSvg
                    isTriggered={Boolean(runtimeState.triggered)}
                    label={d.props['label']?.toString() || 'EMERGENCY STOP'}
                  />
                  {isSelected && <rect width="120" height="120" fill="none" stroke="var(--color-primary)" strokeWidth="6" rx="8" />}
                </svg>
              ) : d.type === 'motor' ? (
                d.props['variant'] === 'fan' ? (
                  <svg width="80" height="80" viewBox="0 0 100 100">
                    <FanSvg
                      isRunning={Boolean(runtimeState.running)}
                      isOverload={runtimeState.load === 'OVERLOAD'}
                      color={d.props['color']?.toString() || '#187AA0'}
                    />
                    {isSelected && <rect width="100" height="100" fill="none" stroke="var(--color-primary)" strokeWidth="4" rx="12" />}
                  </svg>
                ) : d.props['variant'] === 'pump_motor' ? (
                  <svg width="120" height="100" viewBox="0 0 120 100">
                    <PumpSvg
                      isRunning={Boolean(runtimeState.running)}
                      isOverload={runtimeState.load === 'OVERLOAD'}
                      color={d.props['color']?.toString() || '#1e40af'}
                    />
                    {isSelected && <rect width="120" height="100" fill="none" stroke="var(--color-primary)" strokeWidth="4" rx="8" />}
                  </svg>
                ) : d.props['variant'] === 'mixer' ? (
                  (() => {
                    const isAttachedToTank = Boolean(d.props['tankId']);
                    const svgH = isAttachedToTank ? 60 : 130;
                    return (
                      <svg width="50" height={svgH} viewBox={`0 0 50 ${svgH}`}>
                        <MixerSvg
                          isRunning={Boolean(runtimeState.running)}
                          isOverload={runtimeState.load === 'OVERLOAD'}
                          speed={Number(d.props['speed']) || 100}
                          color={d.props['color']?.toString() || '#1e40af'}
                          bladeType={(d.props['bladeType']?.toString() as any) || 'propeller'}
                          isMountedOnTank={isAttachedToTank}
                        />
                        {isSelected && <rect width="50" height={svgH} fill="none" stroke="var(--color-primary)" strokeWidth="3" rx="6" />}
                      </svg>
                    );
                  })()
                ) : (
                  <svg width="150" height="95" viewBox="0 0 451.393 284.124">
                    <AcMotor3PhaseSvg
                      isRunning={Boolean(runtimeState.running)}
                      isOverload={runtimeState.load === 'OVERLOAD'}
                      color={d.props['color']?.toString() || '#146E46'}
                    />
                    {isSelected && <rect width="451.393" height="284.124" fill="none" stroke="var(--color-primary)" strokeWidth="8" rx="16" />}
                  </svg>
                )
              ) : d.type === 'mixer' ? (
                (() => {
                  const isAttachedToTank = Boolean(d.props['tankId']);
                  const svgH = isAttachedToTank ? 60 : 130;
                  return (
                    <svg width="50" height={svgH} viewBox={`0 0 50 ${svgH}`}>
                      <MixerSvg
                        isRunning={Boolean(runtimeState.running)}
                        isOverload={runtimeState.load === 'OVERLOAD'}
                        speed={Number(d.props['speed']) || 100}
                        color={d.props['color']?.toString() || '#1e40af'}
                        bladeType={(d.props['bladeType']?.toString() as any) || 'propeller'}
                        isMountedOnTank={isAttachedToTank}
                      />
                      {isSelected && <rect width="50" height={svgH} fill="none" stroke="var(--color-primary)" strokeWidth="3" rx="6" />}
                    </svg>
                  );
                })()
              ) : d.type === 'fan' ? (
                <svg width="80" height="80" viewBox="0 0 100 100">
                  <FanSvg
                    isRunning={Boolean(runtimeState.running)}
                    isOverload={runtimeState.load === 'OVERLOAD'}
                    speed={Number(d.props['speed']) || 100}
                    color={d.props['color']?.toString() || '#187AA0'}
                  />
                  {isSelected && <rect width="100" height="100" fill="none" stroke="var(--color-primary)" strokeWidth="4" rx="12" />}
                </svg>
              ) : d.type === 'tank' ? (
                (() => {
                  const attachedMixer = devices.find(
                    (m) =>
                      (m.type === 'mixer' || (m.type === 'motor' && m.props['variant'] === 'mixer')) &&
                      m.props['tankId'] === d.id
                  );
                  const mixerRuntime = attachedMixer ? (deviceRuntimeStates[attachedMixer.id] || {}) : null;
                  const attachedSensor = devices.find(
                    (s) => s.type === 'level-sensor' && s.props['tankId'] === d.id
                  );
                  return (
                    <svg width="140" height="180" viewBox="0 0 140 180">
                      <TankSvg
                        levelPercent={Number(runtimeState.levelPercent) || 0}
                        capacity={Number(d.props['capacity']) || 1000}
                        liquidColor={d.props['color']?.toString()}
                        inletCount={Number(d.props['inletCount']) || 1}
                        mixerState={
                          attachedMixer
                            ? {
                                isAttached: true,
                                isRunning: Boolean(mixerRuntime?.running),
                                speed: Number(attachedMixer.props['speed']) || 100,
                                bladeType: (attachedMixer.props['bladeType'] as any) || 'propeller',
                              }
                            : undefined
                        }
                        levelSensorState={
                          attachedSensor
                            ? {
                                isAttached: true,
                                levelPercent: Number(runtimeState.levelPercent) || 0,
                              }
                            : undefined
                        }
                      />
                      {isSelected && <rect width="140" height="180" fill="none" stroke="var(--color-primary)" strokeWidth="4" rx="10" />}
                    </svg>
                  );
                })()
              ) : d.type === 'pump' ? (
                <svg width="120" height="100" viewBox="0 0 120 100">
                  <PumpSvg
                    isRunning={Boolean(runtimeState.running)}
                    isOverload={runtimeState.load === 'OVERLOAD'}
                    color={d.props['color']?.toString() || '#1e40af'}
                  />
                  {isSelected && <rect width="120" height="100" fill="none" stroke="var(--color-primary)" strokeWidth="4" rx="8" />}
                </svg>
              ) : d.type === 'valve' ? (
                <svg width="100" height="100" viewBox="0 0 100 100">
                  <ValveSvg
                    isOpen={Boolean(runtimeState.open)}
                    color={d.props['color']?.toString()}
                    voltage={d.props['voltage']?.toString()}
                    valveType={d.props['valveType']?.toString()}
                    actualFlow={Number(runtimeState.actualFlow) || 0}
                  />
                  {isSelected && <rect width="100" height="100" fill="none" stroke="var(--color-primary)" strokeWidth="4" rx="8" />}
                </svg>
              ) : d.type === 'level-sensor' ? (
                (() => {
                  const isAttachedToTank = Boolean(d.props['tankId'] && devices.some(t => t.type === 'tank' && t.id === d.props['tankId']));
                  const svgH = isAttachedToTank ? 46 : 160;
                  return (
                    <svg width="44" height={svgH} viewBox={`0 0 44 ${svgH}`}>
                      <LevelSensorSvg
                        value={Number(runtimeState.level) || 0}
                        type={d.props['signalType']?.toString() || '0-10V'}
                        rawValue={Number(runtimeState.outputValue) || 0}
                        minLevel={Number(d.props['minLevel']) || 0}
                        maxLevel={Number(d.props['maxLevel']) || 1000}
                        color={d.props['color']?.toString() || '#0284c7'}
                        isMountedOnTank={isAttachedToTank}
                        isFaulted={runtimeState.signalStatus === 'WIRE_BREAK'}
                      />
                      {isSelected && <rect width="44" height={svgH} fill="none" stroke="var(--color-primary)" strokeWidth="3" rx="6" />}
                    </svg>
                  );
                })()
              ) : d.type === 'pid-controller' ? (
                <svg width="100" height="80" viewBox="0 0 100 80">
                  <PIDSvg pv={Number(runtimeState.pv) || 0} sp={Number(runtimeState.sp) || 0} cv={Number(runtimeState.cv) || 0} mode={String(runtimeState.workMode) || 'MANUAL'} />
                  {isSelected && <rect width="100" height="80" fill="none" stroke="var(--color-primary)" strokeWidth="4" rx="8" />}
                </svg>
              ) : d.type === 'sensor' ? (
                (() => {
                  const variant = (d.props['variant']?.toString() as any) || 'photoelectric';
                  const range = Number(d.props['range']) || 80;
                  const detected = Boolean(runtimeState.detected);
                  const color = d.props['color']?.toString() || '#0284c7';
                  const svgW = variant === 'light_curtain' ? 84 : 44;
                  const svgH = Math.max(60, range + 44);

                  return (
                    <g>
                      <SensorSvg
                        variant={variant}
                        detected={detected}
                        range={range}
                        color={color}
                      />
                      {isSelected && (
                        <rect
                          width={svgW}
                          height={svgH}
                          fill="none"
                          stroke="var(--color-primary)"
                          strokeWidth="3"
                          rx="6"
                        />
                      )}
                    </g>
                  );
                })()
              ) : (
                <>
                  <rect
                    width="60"
                    height="60"
                    fill="var(--color-bg-elevated)"
                    stroke={isSelected ? 'var(--color-primary)' : color}
                    strokeWidth={isSelected ? 2 : 1}
                    rx="8"
                  />
                  <text x="30" y="35" fill={color} fontSize="12" textAnchor="middle" pointerEvents="none">
                    {text}
                  </text>
                </>
              )}
              {(() => {
                const anchor = getBadgeAnchor(d);
                const nameAnchor = getNameBadgeAnchor(d);
                return (
                  <>
                    <AddressBadge
                      address={d.props['address']?.toString()}
                      cx={anchor.cx}
                      anchorX={anchor.anchorX}
                      anchorY={anchor.anchorY}
                      offsetX={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'address' ? activeLabelDrag.liveOffset.x : Number(d.props['labelOffsetX']) || 0}
                      offsetY={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'address' ? activeLabelDrag.liveOffset.y : Number(d.props['labelOffsetY']) || 0}
                      visible={d.props['showLabel'] !== false}
                      isDesignMode={isDesignMode}
                      isSelected={isSelected}
                      isDragging={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'address'}
                      onPointerDown={(e) =>
                        handleLabelPointerDown(
                          e,
                          d.id,
                          'address',
                          Number(d.props['labelOffsetX']) || 0,
                          Number(d.props['labelOffsetY']) || 0
                        )
                      }
                      onReset={() => handleResetLabelOffset(d.id, 'address')}
                      onSelect={() => select(d.id, d.type)}
                    />
                    <NameBadge
                      name={d.name}
                      cx={nameAnchor.cx}
                      cy={nameAnchor.cy}
                      anchorX={nameAnchor.anchorX}
                      anchorY={nameAnchor.anchorY}
                      offsetX={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'name' ? activeLabelDrag.liveOffset.x : Number(d.props['nameOffsetX']) || 0}
                      offsetY={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'name' ? activeLabelDrag.liveOffset.y : Number(d.props['nameOffsetY']) || 0}
                      visible={d.props['showNameLabel'] !== false}
                      isDesignMode={isDesignMode}
                      isSelected={isSelected}
                      isDragging={activeLabelDrag?.deviceId === d.id && activeLabelDrag.target === 'name'}
                      onPointerDown={(e) =>
                        handleLabelPointerDown(
                          e,
                          d.id,
                          'name',
                          Number(d.props['nameOffsetX']) || 0,
                          Number(d.props['nameOffsetY']) || 0
                        )
                      }
                      onReset={() => handleResetLabelOffset(d.id, 'name')}
                      onSelect={() => select(d.id, d.type)}
                    />
                  </>
                );
              })()}
            </g>
          );
        })}

        {/* Render Products */}
        {activeMode !== 'design' && products.map((p: any) => {
          if (!p.active) return null;
          const isBlocked = p.blocked;
          return (
            <g
              key={p.id}
              transform={`translate(${p.x}, ${p.y})`}
              onClick={(e) => {
                e.stopPropagation();
                processEngine.removeProduct(p.id);
                useSimulationStore.getState().updateSnapshot();
              }}
              style={{ cursor: 'pointer' }}
            >
              <title>{isBlocked ? '⚠️ Sıkışan Ürünü Hattan Al (Tıkla)' : '📦 Ürünü Hattan Kaldır (Tıkla)'}</title>
              <rect
                width={p.width}
                height={p.height}
                fill={isBlocked ? "var(--color-danger)" : "var(--color-warning)"}
                stroke={isBlocked ? "#ffffff" : "var(--color-text)"}
                strokeWidth={isBlocked ? 2.5 : 1.5}
                rx="4"
                style={{
                  filter: isBlocked ? 'drop-shadow(0 0 6px rgba(239, 68, 68, 0.85))' : 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))',
                }}
              />
              <text
                x={p.width / 2}
                y={p.height / 2 + 4}
                fill={isBlocked ? "#ffffff" : "var(--color-bg-base)"}
                fontSize="10"
                textAnchor="middle"
                fontWeight="bold"
                pointerEvents="none"
              >
                {isBlocked ? '■!' : 'BOX'}
              </text>
            </g>
          );
        })}

        {/* Interactive Pipe Ports (Design Mode) */}
        {isDesignMode &&
          devices.map((d) => {
            const ports = getDevicePorts(d);
            const posX = d.transform?.position?.x ?? 0;
            const posY = d.transform?.position?.y ?? 0;

            return ports.map((p) => {
              const wx = posX + p.relativePos.x;
              const wy = posY + p.relativePos.y;
              const isStartPort =
                pipeDrawingStart?.deviceId === d.id &&
                (pipeDrawingStart.portId ? pipeDrawingStart.portId === p.id : pipeDrawingStart?.portType === p.portType);
              const isHovered =
                hoveredPort?.deviceId === d.id &&
                (hoveredPort.portId ? hoveredPort.portId === p.id : hoveredPort?.portType === p.portType);
              const isDrawing = Boolean(pipeDrawingStart);

              return (
                <g
                  key={p.id}
                  transform={`translate(${wx}, ${wy})`}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => handlePortClick(e, d.id, p.portType, p.id)}
                  onMouseEnter={() => setHoveredPort({ deviceId: d.id, portType: p.portType, portId: p.id })}
                  onMouseLeave={() => setHoveredPort(null)}
                >
                  {/* Outer Pulsing Halo */}
                  {(isDrawing || isHovered || isStartPort) && (
                    <circle
                      cx="0"
                      cy="0"
                      r={isHovered ? 12 : 9}
                      fill={isStartPort ? 'rgba(59, 130, 246, 0.35)' : isHovered ? 'rgba(34, 197, 94, 0.4)' : 'rgba(56, 189, 248, 0.25)'}
                      stroke={isStartPort ? '#3b82f6' : isHovered ? '#22c55e' : '#38bdf8'}
                      strokeWidth={1.5}
                      strokeDasharray={isStartPort ? undefined : '3,2'}
                    />
                  )}

                  {/* Port Pin Collar */}
                  <circle
                    cx="0"
                    cy="0"
                    r={5}
                    fill="#0f172a"
                    stroke={isStartPort ? '#3b82f6' : isHovered ? '#22c55e' : '#94a3b8'}
                    strokeWidth={1.5}
                  />
                  {/* Inner Dot */}
                  <circle
                    cx="0"
                    cy="0"
                    r={2.5}
                    fill={isStartPort ? '#60a5fa' : isHovered ? '#4ade80' : '#38bdf8'}
                  />

                  {/* Tooltip Badge on hover */}
                  {isHovered && (
                    <g transform="translate(0, -18)" style={{ pointerEvents: 'none' }}>
                      <rect
                        x="-48"
                        y="-10"
                        width="96"
                        height="18"
                        rx="4"
                        fill="#0f172a"
                        stroke={isStartPort ? '#3b82f6' : '#22c55e'}
                        strokeWidth="1"
                      />
                      <text
                        x="0"
                        y="3"
                        fill="#f8fafc"
                        fontSize="10"
                        fontWeight="bold"
                        textAnchor="middle"
                      >
                        {isStartPort
                          ? 'İptal Et'
                          : isDrawing
                            ? 'Bağlantı Kur'
                            : p.name}
                      </text>
                    </g>
                  )}
                </g>
              );
            });
          })}
        </g>
      </svg>

      {/* Pipe Drawing Active Notification Banner */}
      {pipeDrawingStart && (
        <div
          style={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(15, 23, 42, 0.94)',
            border: '1px solid #38bdf8',
            borderRadius: '8px',
            padding: '8px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            zIndex: 100,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            color: '#f8fafc',
            fontSize: '13px',
            fontWeight: 500,
            backdropFilter: 'blur(8px)',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#38bdf8',
              boxShadow: '0 0 8px #38bdf8',
            }}
          />
          <span>Boru Hattı Çizimi Aktif: Bağlantıyı tamamlamak için hedef boru portuna tıklayın</span>
          <button
            type="button"
            onClick={() => setPipeDrawingStart(null)}
            style={{
              padding: '4px 10px',
              backgroundColor: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid #ef4444',
              borderRadius: '4px',
              color: '#fca5a5',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            İptal (ESC)
          </button>
        </div>
      )}

      {/* Zoom Controls Overlay */}
      <div className={styles.zoomControls}>
        <button
          type="button"
          className={styles.zoomBtn}
          onClick={handleZoomOut}
          title={`${t('scene.zoom.out')} (-)`}
          aria-label="Zoom Out"
        >
          <Icon name="zoom-out" size={15} />
        </button>
        <button
          type="button"
          className={styles.zoomResetBtn}
          onClick={handleResetZoom}
          title={`${t('scene.zoom.reset')} (Ctrl+0)`}
          aria-label="Reset Zoom"
        >
          {Math.round(viewport.zoom * 100)}%
        </button>
        <button
          type="button"
          className={styles.zoomBtn}
          onClick={handleZoomIn}
          title={`${t('scene.zoom.in')} (+)`}
          aria-label="Zoom In"
        >
          <Icon name="zoom-in" size={15} />
        </button>
        <div className={styles.zoomDivider} />
        <button
          type="button"
          className={styles.zoomBtn}
          onClick={handleResetZoom}
          title={t('scene.zoom.fit')}
          aria-label="Fit View"
        >
          <Icon name="zoom-reset" size={15} />
        </button>
      </div>

      <span className={styles.modeLabel}>
        <Icon name="mode-design" size={16} />
        {t('workspace.design.title')}
      </span>

      {devices.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>{t('scene.empty.title')}</p>
          <p className={styles.emptyDescription}>{t('scene.empty.description')}</p>
        </div>
      )}

      {/* Action panel */}
      <div style={{ position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8, zIndex: 10 }}>
        {hasHMI && (
          <button
            type="button"
            onClick={() => toggleHmiOverlay()}
            style={{
              padding: '8px 14px',
              backgroundColor: hmiOverlayOpen ? 'rgba(59, 130, 246, 0.25)' : 'rgba(31, 41, 55, 0.85)',
              color: hmiOverlayOpen ? '#60a5fa' : 'var(--color-text)',
              border: `1px solid ${hmiOverlayOpen ? '#3b82f6' : 'rgba(255, 255, 255, 0.15)'}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '12px',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              transition: 'all 0.15s ease',
            }}
            title="HMI Operatör Panelini Aç / Kapat"
          >
            <Icon name="app" size={15} />
            HMI Paneli {hmiOverlayOpen ? '✓' : ''}
          </button>
        )}
        {isRunning && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => {
                // Spawn product on the first conveyor found
                const conv = devices.find((x) => x.type === 'conveyor');
                if (conv) {
                  const posX = conv.transform?.position?.x ?? 0;
                  const posY = conv.transform?.position?.y ?? 0;
                  processEngine.spawnProduct(posX + 10, posY + 10);
                  useSimulationStore.getState().updateSnapshot();
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-text)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              }}
            >
              + Spawn Product
            </button>
            {hasJammed && (
              <button
                type="button"
                onClick={() => {
                  processEngine.clearJams();
                  useSimulationStore.getState().updateSnapshot();
                }}
                style={{
                  padding: '8px 14px',
                  backgroundColor: 'var(--color-danger)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 8px rgba(239, 68, 68, 0.45)',
                }}
                title="Sıkışan ürünleri hattan temizle"
              >
                ⚠️ Sıkışmayı Gider
              </button>
            )}
            {products.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  processEngine.clearAllProducts();
                  useSimulationStore.getState().updateSnapshot();
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'var(--color-bg-elevated)',
                  color: 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
                title="Hattaki tüm ürünleri temizle"
              >
                Hattı Boşalt
              </button>
            )}
          </div>
        )}
      </div>

      {/* Floating pipe drawing helper HUD */}
      {pipeDrawingStart && (
        <div
          style={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 40,
            background: 'rgba(15, 23, 42, 0.92)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(56, 189, 248, 0.45)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.55)',
            borderRadius: '9999px',
            padding: '7px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            color: '#f8fafc',
            fontSize: '13px',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: '#38bdf8',
              boxShadow: '0 0 10px #38bdf8',
            }}
          />
          <span>
            <strong>Boru Hattı Çizimi:</strong> Boş alana tıklayarak <strong>Dirsek (Köşe)</strong> ekleyin. Bitirmek için hedef porta tıklayın.
          </span>
          <span
            style={{
              color: '#94a3b8',
              fontSize: '11.5px',
              borderLeft: '1px solid #334155',
              paddingLeft: '10px',
            }}
          >
            Esc / Sağ Tık: İptal | Backspace: Son Dirseği Kaldır
            {pipeDrawingWaypoints.length > 0 && ` (${pipeDrawingWaypoints.length} dirsek)`}
          </span>
        </div>
      )}
    </div>
  );
}
